import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AssessmentDetail from './AssessmentDetail';
import AssessmentsList from './AssessmentsList';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Card from '@/components/Card';
import Button from '@/components/Button';
import { Body, Heading } from '@/components/Typography';
import Chip from '@/components/Chip';
import {
  Assessment,
  AssessmentDetail as AssessmentDetailType,
  AssessmentsAPI,
  PaginationInfo,
} from '@/services/assessmentsAPI';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import LoadingState from '@/components/LoadingState';

const AiAssessments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | undefined>();

  // State for direct assessment view (when externalId is in URL)
  const [directAssessment, setDirectAssessment] =
    useState<AssessmentDetailType | null>(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);

  // Get params from URL query params
  const queryParams = new URLSearchParams(location.search);
  const externalIdFromUrl = queryParams.get('externalId');
  const reviewedParam = queryParams.get('reviewed');
  const includePendingParam = queryParams.get('include_pending');
  const searchParam = queryParams.get('search');
  const skillNameParam = queryParams.get('skill_name');
  const resultStatusParam = queryParams.get('result_status');
  const pageParam = queryParams.get('page');
  const sortParam = queryParams.get('sort');
  const reviewed = reviewedParam === 'true';
  const includePending = includePendingParam === 'true';
  const search = searchParam || '';
  const skillName = skillNameParam || '';
  const resultStatus = resultStatusParam || undefined;
  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const sortOrder: 'asc' | 'desc' = sortParam === 'created_at' ? 'asc' : 'desc';

  // Local state for immediate input values (for responsive UI)
  const [localSearch, setLocalSearch] = useState(search);
  const [localSkillName, setLocalSkillName] = useState(skillName);

  // Debounce the search values (500ms delay)
  const debouncedSearch = useDebouncedValue(localSearch, 500);
  const debouncedSkillName = useDebouncedValue(localSkillName, 500);

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    setLocalSkillName(skillName);
  }, [skillName]);

  // Update URL params when debounced values change
  useEffect(() => {
    if (debouncedSearch !== search) {
      const newParams = new URLSearchParams(location.search);
      if (debouncedSearch) {
        newParams.set('search', debouncedSearch);
      } else {
        newParams.delete('search');
      }
      newParams.delete('page');
      newParams.delete('externalId');
      navigate(`${location.pathname}?${newParams.toString()}`);
    }
  }, [debouncedSearch, search, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (debouncedSkillName !== skillName) {
      const newParams = new URLSearchParams(location.search);
      if (debouncedSkillName) {
        newParams.set('skill_name', debouncedSkillName);
      } else {
        newParams.delete('skill_name');
      }
      newParams.delete('page');
      newParams.delete('externalId');
      navigate(`${location.pathname}?${newParams.toString()}`);
    }
  }, [
    debouncedSkillName,
    skillName,
    location.search,
    location.pathname,
    navigate,
  ]);

  // Load direct assessment when externalId is in URL (skip list loading)
  useEffect(() => {
    if (externalIdFromUrl) {
      loadDirectAssessment(externalIdFromUrl);
    } else {
      // Clear direct assessment state when going back to list
      setDirectAssessment(null);
      setDirectError(null);
    }
  }, [externalIdFromUrl]);

  // Load assessments list when filters change (only when NOT in direct view mode)
  useEffect(() => {
    if (!externalIdFromUrl) {
      loadAssessments();
    }
  }, [
    reviewed,
    includePending,
    search,
    skillName,
    resultStatus,
    page,
    sortParam,
    externalIdFromUrl,
  ]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const sortValue = sortParam || '-created_at';
      const response = await AssessmentsAPI.getAssessments({
        reviewed: reviewed,
        include_pending: includePending,
        search: search || undefined,
        skill_name: skillName || undefined,
        result_status: resultStatus,
        page: page,
        per_page: 10,
        sort: sortValue as 'created_at' | '-created_at',
      });
      setAssessments(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load assessments');
      console.error('Error loading assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDirectAssessment = async (externalId: string) => {
    try {
      setDirectLoading(true);
      setDirectError(null);
      const response = await AssessmentsAPI.getAssessment(externalId);
      setDirectAssessment(response.data);
    } catch (err) {
      console.error('Error loading assessment:', err);
      setDirectError('Failed to load assessment');
    } finally {
      setDirectLoading(false);
    }
  };

  const handleSelectAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    // Note: Don't add externalId to URL when selecting from list
    // to keep the list visible. Direct links with externalId show detail-only view.
  };

  const handleReviewedToggle = (newReviewed: boolean) => {
    const newParams = new URLSearchParams(location.search);
    if (newReviewed) {
      newParams.set('reviewed', 'true');
      newParams.delete('include_pending'); // Clear pending filter since reviewed items can't be pending
    } else {
      newParams.delete('reviewed');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleIncludePendingToggle = (newIncludePending: boolean) => {
    const newParams = new URLSearchParams(location.search);
    if (newIncludePending) {
      newParams.set('include_pending', 'true');
    } else {
      newParams.delete('include_pending');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleSearchChange = (newSearch: string) => {
    setLocalSearch(newSearch);
  };

  const handleSkillFilterChange = (newSkillName: string) => {
    setLocalSkillName(newSkillName);
  };

  const handleResultStatusFilterChange = (
    newResultStatus: string | undefined,
  ) => {
    const newParams = new URLSearchParams(location.search);
    if (newResultStatus) {
      newParams.set('result_status', newResultStatus);
    } else {
      newParams.delete('result_status');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('page', newPage.toString());
    newParams.delete('externalId'); // Clear selection when changing pages
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleSortChange = (newSortOrder: 'asc' | 'desc') => {
    const newParams = new URLSearchParams(location.search);
    const sortValue = newSortOrder === 'desc' ? '-created_at' : 'created_at';
    newParams.set('sort', sortValue);
    newParams.delete('page'); // Reset to page 1 when sorting
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleReviewSuccess = () => {
    // Reload assessments list after review
    loadAssessments();
    setSelectedAssessment(null);
    navigate(location.pathname);
  };

  const handleClearExternalId = () => {
    // Clear externalId and refresh the page
    const newParams = new URLSearchParams(location.search);
    newParams.delete('externalId');
    const newUrl = `${location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
    window.location.href = newUrl;
  };

  const handleDirectReviewSuccess = () => {
    // After review in direct mode, clear externalId and refresh
    handleClearExternalId();
  };

  // Direct view mode - show only the assessment detail with filter tag
  if (externalIdFromUrl) {
    return (
      <Page title="AI Assessment" size="fullWidth" headerPadding>
        <Stack vertical gap={16} css={{ padding: '0 15px' }}>
          <Stack gap={8} align="center">
            <Body>Viewing:</Body>
            <Chip
              label={`Assessment ID: ${externalIdFromUrl}`}
              color="primary"
              onDelete={handleClearExternalId}
            />
          </Stack>
          <Card changeStyle={{ padding: 16 }}>
            {directLoading ? (
              <LoadingState text="Loading assessment..." />
            ) : directError ? (
              <Stack
                vertical
                align="center"
                justify="center"
                css={{ minHeight: '400px' }}
              >
                <Heading>Error Loading Assessment</Heading>
                <Body>{directError}</Body>
                <Button
                  onClick={() => loadDirectAssessment(externalIdFromUrl)}
                  a11yLabel="Retry loading assessment"
                  label="Retry"
                />
              </Stack>
            ) : directAssessment ? (
              <AssessmentDetail
                assessment={directAssessment}
                onReviewSuccess={handleDirectReviewSuccess}
              />
            ) : null}
          </Card>
        </Stack>
      </Page>
    );
  }

  // List view mode - show list and detail panels
  return (
    <Page title="AI Assessments" size="fullWidth" headerPadding>
      <Stack
        gap={16}
        css={{ minHeight: '600px', padding: '0 15px' }}
        style={{ alignItems: 'flex-start' }}
      >
        <div style={{ flex: '0 0 400px' }}>
          <AssessmentsList
            assessments={assessments}
            selectedAssessment={selectedAssessment}
            onSelectAssessment={handleSelectAssessment}
            loading={loading}
            error={error}
            onRefresh={loadAssessments}
            pagination={pagination}
            onPageChange={handlePageChange}
            reviewed={reviewed}
            onReviewedToggle={handleReviewedToggle}
            includePending={includePending}
            onIncludePendingToggle={handleIncludePendingToggle}
            search={localSearch}
            onSearchChange={handleSearchChange}
            skillName={localSkillName}
            onSkillFilterChange={handleSkillFilterChange}
            resultStatus={resultStatus}
            onResultStatusFilterChange={handleResultStatusFilterChange}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Card changeStyle={{ padding: 16 }}>
            {selectedAssessment ? (
              <AssessmentDetail
                assessment={selectedAssessment}
                onReviewSuccess={handleReviewSuccess}
              />
            ) : (
              <Stack
                vertical
                align="center"
                justify="center"
                css={{ minHeight: '400px' }}
              >
                <Heading>No Assessment Selected</Heading>
                <Body>Select an assessment from the list to view details</Body>
              </Stack>
            )}
          </Card>
        </div>
      </Stack>
    </Page>
  );
};

export default AiAssessments;
